import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { MetricEntry, MetricKey } from "@/lib/metrics/types";
import { usePracticeMode } from "@/hooks/usePracticeMode";
import { getPractice } from "@/lib/practice/store";
import { assertRealMetricWrite, chronological, recordedAt } from "@/lib/metrics/history";
import { currentSnapshot } from "@/lib/metrics/accounts";

/** Practice mode: savings/debt/FICO come from practice check-ins instead of real logs. */
function practiceOverlay(real: MetricEntry[]): MetricEntry[] {
  const money = new Set(["savings", "debt", "fico"]);
  const rows: MetricEntry[] = real.filter((e) => !money.has(e.metric));
  getPractice().checkins.forEach((c, i) => {
    const at = new Date(c.week_start + "T12:00:00").toISOString();
    const add = (metric: string, value: number | null | undefined) => {
      if (value == null) return;
      rows.push({ id: `practice-${metric}-${c.week_start}`, user_id: "practice", metric, value, recorded_at: at, is_baseline: i === 0, details: null } as unknown as MetricEntry);
    };
    add("savings", Number(c.savings));
    add("debt", Number(c.total_debt));
    add("fico", c.fico != null ? Number(c.fico) : null);
  });
  return rows.sort((a, b) => a.recorded_at.localeCompare(b.recorded_at));
}

// metric_entries is staged for this release; cast until generated types include it.
const db = supabase as any;

export function useMetricEntries(userId?: string) {
  const { user: me } = useAuth();
  const user = userId ? { id: userId } : me;
  const practice = usePracticeMode() && !userId;
  return useQuery({
    queryKey: ["metric-entries", user?.id, practice],
    enabled: !!user,
    queryFn: async (): Promise<MetricEntry[]> => {
      if (!user) throw new Error("Not signed in");
      const { data, error } = await db
        .from("metric_entries")
        .select("*")
        .eq("user_id", user.id)
        .order("recorded_at", { ascending: true });
      if (error) throw error;
      return practice ? practiceOverlay((data ?? []) as MetricEntry[]) : ((data ?? []) as MetricEntry[]);
    },
  });
}

export function summarize(entries: MetricEntry[], metric: MetricKey) {
  const rows = chronological(entries.filter((e) => e.metric === metric));
  const baseline = rows.find((r) => r.is_baseline) ?? rows[0] ?? null;
  const latest = metric === "debt" || metric === "savings" ? currentSnapshot(rows) : rows[rows.length - 1] ?? null;
  const ninetyAgo = Date.now() - 90 * 864e5;
  const window90 = rows.filter((r) => new Date(r.recorded_at).getTime() >= ninetyAgo);
  return {
    rows,
    baseline,
    latest,
    delta: baseline && latest ? Number(latest.value) - Number(baseline.value) : 0,
    delta90: window90.length > 1 ? Number(window90[window90.length - 1].value) - Number(window90[0].value) : 0,
  };
}

export function useAddMetricEntry() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const practice = usePracticeMode();
  return useMutation({
    mutationFn: async (input: { metric: MetricKey; value: number; date?: string; details?: Record<string, any>; file?: File | null; filePath?: string | null }) => {
      if (!user) throw new Error("Not signed in");
      assertRealMetricWrite(practice, input.metric);
      const recorded_at = input.date ? recordedAt(input.date) : new Date().toISOString();
      let file_path: string | null = input.filePath ?? null;
      if (input.file) {
        const ext = input.file.name.split(".").pop() || "jpg";
        file_path = `${user.id}/${input.metric}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("metric-uploads").upload(file_path, input.file);
        if (upErr) throw upErr;
      }
      const { data, error } = await db.from("metric_entries").insert({
        user_id: user.id,
        metric: input.metric,
        value: input.value,
        details: input.details ?? {},
        file_path,
        recorded_at,
      }).select("*").single();
      if (error) throw error;
      return data as MetricEntry;
    },
    onSuccess: async (row) => {
      qc.setQueryData<MetricEntry[]>(["metric-entries", row.user_id, false], (rows) => chronological([...(rows ?? []).filter((entry) => entry.id !== row.id), row]));
      await qc.invalidateQueries({ queryKey: ["metric-entries", row.user_id] });
      qc.invalidateQueries({ queryKey: ["money-foundation"] }); qc.invalidateQueries({ queryKey: ["desk-career"] });
    },
  });
}
